import { EnvironmentInjector, Injectable, Signal, WritableSignal, effect, inject, runInInjectionContext, signal, untracked } from '@angular/core';
import type { PendingFile } from '../agent.types';
import type { AgentChatAdapter } from './agent.chat.adapter';
import { DurableWorkbuddyAgentChatAdapter } from './durable.workbuddy.agent.chat.adapter';

export interface BackgroundThreadState {
  streaming: boolean;
  unread: boolean;
}

interface PoolEntry {
  adapter: AgentChatAdapter;
  detachedAt: number;
  cleanup: () => void;
}

const MAX_BACKGROUND = 3;

@Injectable({ providedIn: 'root' })
export class BackgroundStreamManager {

  private readonly envInjector = inject(EnvironmentInjector);
  private readonly pool = new Map<string, PoolEntry>();
  /**
   * Threads whose pooled durable adapter was released. The chat that sent it to the background cached
   * the transcript as it stood at departure, and the released adapter held everything after; reopening
   * from that cache showed a half-written reply, so the chat re-reads these threads from storage.
   */
  private readonly releasedThreadIds = new Set<string>();

  private readonly _threadStates: WritableSignal<Map<string, BackgroundThreadState>> = signal(new Map());
  public readonly $threadStates: Signal<Map<string, BackgroundThreadState>> = this._threadStates.asReadonly();

  private readonly _lastCompleted: WritableSignal<{ threadId: string; at: number } | null> = signal(null);
  public readonly $lastCompleted: Signal<{ threadId: string; at: number } | null> = this._lastCompleted.asReadonly();

  /**
   * Keeps a chat's adapter alive after its container lets go of it. `closing` is for a chat whose
   * tab was just closed but whose send is still unanswered: it is kept only until that answer, then
   * let go quietly, or kept on if the answer was a refusal whose words live nowhere else.
   */
  public detach(threadId: string, adapter: AgentChatAdapter, options: { closing?: boolean } = {}): void {

    // Evict oldest if at capacity
    if (this.pool.size >= MAX_BACKGROUND) {
      this.evictOldest();
    }

    // A second detach of the same thread replaces the entry: the old monitor goes, and an older
    // adapter is released, or it would keep its subscription and effect alive with nothing holding it.
    const existing = this.pool.get(threadId);
    this.releasedThreadIds.delete(threadId);
    if (existing?.adapter === adapter) existing.cleanup();
    else if (existing) this.cancel(threadId);

    // Start monitoring adapter status
    const cleanup = options.closing ? this.releaseWhenAnswered(threadId, adapter) : this.monitorAdapter(threadId, adapter);

    this.pool.set(threadId, { adapter, detachedAt: Date.now(), cleanup });

    // Set initial state based on current adapter status
    const status = adapter.status;
    const streaming = status === 'streaming' || status === 'submitted';
    this.updateThreadState(threadId, { streaming, unread: !streaming });
  }

  public reattach(threadId: string): AgentChatAdapter | null {

    const entry = this.pool.get(threadId);
    if (!entry) return null;

    entry.cleanup();
    this.pool.delete(threadId);
    this.removeThreadState(threadId);
    this.releasedThreadIds.delete(threadId);

    return entry.adapter;
  }

  /**
   * True once after the thread's background adapter was released: the caller's cached transcript of
   * it is older than what that adapter held, and must be dropped for a read from storage.
   */
  public takeReleased(threadId: string): boolean {

    return this.releasedThreadIds.delete(threadId);
  }

  public markRead(threadId: string): void {

    const current = this._threadStates().get(threadId);
    if (!current) return;

    if (!this.pool.has(threadId)) {
      this.removeThreadState(threadId);
    } else {
      this.updateThreadState(threadId, { ...current, unread: false });
    }
  }

  /**
   * Drop a backgrounded adapter. The request-bound adapter loses its stream with the browser
   * either way; the durable adapter only closes its subscription and the run keeps executing on
   * the server, so this is a release of what the browser holds, never a Stop. With `adapter`, only
   * that adapter is dropped, so a caller tidying up its own hand-off never drops a newer one.
   */
  public cancel(threadId: string, adapter?: AgentChatAdapter): void {

    const entry = this.pool.get(threadId);
    if (!entry) return;
    if (adapter && entry.adapter !== adapter) return;

    entry.cleanup();
    entry.adapter.dispose();
    this.pool.delete(threadId);
    this.removeThreadState(threadId);
  }

  /**
   * Release a pooled durable chat's live subscription while keeping its unread state. Its run lives
   * on the server, so nothing is lost: opening the chat again reads it back from storage. Called when
   * a background run finishes and when its tab is closed. A request-bound adapter is left alone,
   * because releasing it would cut its run, and so is one with a send still unanswered or words the
   * server refused, which live nowhere else (a closing tab takes those first, see `takeUnsent`).
   */
  public releaseConnection(threadId: string, adapter?: AgentChatAdapter): void {

    const entry = this.pool.get(threadId);
    if (!entry || !(entry.adapter instanceof DurableWorkbuddyAgentChatAdapter)) return;
    if (adapter && entry.adapter !== adapter) return;
    if (BackgroundStreamManager.holdsUnsentWords(entry.adapter) || BackgroundStreamManager.awaitsSendAnswer(entry.adapter)) return;
    entry.cleanup();
    entry.adapter.dispose();
    this.pool.delete(threadId);
    this.releasedThreadIds.add(threadId);
    // A run that finished already left the chat marked unread here. One released mid-run (its tab was
    // closed) has no monitor left to see it finish, so its working mark would stay in the list for ever.
    if (this._threadStates().get(threadId)?.streaming) this.removeThreadState(threadId);
  }

  /**
   * Hands over, once, what a pooled durable chat holds unsent: the words of a request the server
   * refused or guidance a stopped run handed back, and the files of a send that never travelled. The
   * tab bar calls it when the chat's tab is closed, to keep them for that thread's composer, since
   * nothing else will show them once the chat is let go.
   */
  public takeUnsent(threadId: string): { words: string | null; files: PendingFile[] } {

    const adapter = this.pool.get(threadId)?.adapter;
    if (!(adapter instanceof DurableWorkbuddyAgentChatAdapter)) return { words: null, files: [] };
    const words = adapter.composerRestore ?? adapter.failedSend?.text ?? null;
    const files = adapter.heldUploads;
    adapter.acknowledgeComposerRestore();
    adapter.clearFailedSend();
    adapter.acknowledgeAttachmentsHeld();
    return { words, files };
  }

  public cancelAll(): void {

    const ids = [...this.pool.keys()];
    for (const id of ids) {
      this.cancel(id);
    }
  }

  private evictOldest(): void {

    let oldestId: string | null = null;
    let oldestTime = Infinity;

    for (const [id, entry] of this.pool) {
      // Words the server refused live only on their adapter, so it is never the one pushed out; when
      // every pooled chat holds some, the pool grows past its cap rather than lose them. A send still
      // waiting for its answer counts too: disposing the adapter would drop that answer unseen.
      if (BackgroundStreamManager.holdsUnsentWords(entry.adapter) || BackgroundStreamManager.awaitsSendAnswer(entry.adapter)) continue;
      if (entry.detachedAt < oldestTime) {
        oldestTime = entry.detachedAt;
        oldestId = id;
      }
    }

    if (oldestId) {
      // A durable run carries on on the server after its adapter goes, so the chat's departure cache
      // is stale the same way a released one is.
      const durable = this.pool.get(oldestId)?.adapter instanceof DurableWorkbuddyAgentChatAdapter;
      this.cancel(oldestId);
      if (durable) this.releasedThreadIds.add(oldestId);
    }
  }

  /** For a closed chat: waits for its send to be answered, then lets it go unless that left words to keep. */
  private releaseWhenAnswered(threadId: string, adapter: AgentChatAdapter): () => void {

    let destroyed = false;
    let effectRef: ReturnType<typeof effect> | null = null;
    queueMicrotask(() => {
      if (destroyed) return;
      effectRef = runInInjectionContext(this.envInjector, () => effect(() => {
        if (destroyed || BackgroundStreamManager.awaitsSendAnswer(adapter)) return;
        if (untracked(() => BackgroundStreamManager.holdsUnsentWords(adapter))) return;
        queueMicrotask(() => {
          this.releaseConnection(threadId, adapter);
          if (!this.pool.has(threadId)) this.removeThreadState(threadId);
        });
      }));
    });
    return () => {
      destroyed = true;
      effectRef?.destroy();
    };
  }

  private monitorAdapter(threadId: string, adapter: AgentChatAdapter): () => void {

    let destroyed = false;
    let finished = false;
    let effectRef: ReturnType<typeof effect> | null = null;

    // Defer effect creation to escape the current reactive context (NG0602).
    queueMicrotask(() => {
      if (destroyed) return;

      effectRef = runInInjectionContext(this.envInjector, () => {
        return effect(() => {
          if (destroyed) return;

          const status = adapter.status;
          const streaming = status === 'streaming' || status === 'submitted';
          // Tracked, so a send still out when the run finished re-runs this once it is answered.
          const sendPending = BackgroundStreamManager.awaitsSendAnswer(adapter);
          const current = untracked(() => this._threadStates().get(threadId));

          if (streaming) {
            finished = false;
            this.updateThreadState(threadId, { streaming: true, unread: false });
            return;
          }
          if (current?.streaming) {
            finished = true;
            this.updateThreadState(threadId, { streaming: false, unread: true });
            const holdsUnsentWords = untracked(() => BackgroundStreamManager.holdsUnsentWords(adapter));
            if (!adapter.hasPendingApproval && !adapter.error && !holdsUnsentWords) {
              this._lastCompleted.set({ threadId, at: Date.now() });
            }
          }
          // Finished with no card waiting: a durable chat would otherwise hold its subscription
          // open for as long as the tab lives. Released after this run, outside the effect. One
          // still holding words the server refused, or a send not yet answered, stays pooled: those
          // words live only on the adapter, and reopening the chat hands them back from there.
          const holdsUnsentWords = untracked(() => BackgroundStreamManager.holdsUnsentWords(adapter));
          if (finished && !adapter.hasPendingApproval && !holdsUnsentWords && !sendPending) queueMicrotask(() => this.releaseConnection(threadId, adapter));
        });
      });
    });

    return () => {
      destroyed = true;
      effectRef?.destroy();
    };
  }

  /** True while a durable adapter holds words that never reached the server: a refused send, or guidance handed back. */
  private static holdsUnsentWords(adapter: AgentChatAdapter): boolean {

    return adapter instanceof DurableWorkbuddyAgentChatAdapter && (adapter.composerRestore !== null || adapter.failedSend !== null);
  }

  /** True while a durable adapter's send has left and the route has not answered it yet. */
  private static awaitsSendAnswer(adapter: AgentChatAdapter): boolean {

    return adapter instanceof DurableWorkbuddyAgentChatAdapter && adapter.sendPending;
  }

  private updateThreadState(threadId: string, state: BackgroundThreadState): void {

    this._threadStates.update(map => {
      const next = new Map(map);
      next.set(threadId, state);
      return next;
    });
  }

  private removeThreadState(threadId: string): void {

    this._threadStates.update(map => {
      const next = new Map(map);
      next.delete(threadId);
      return next;
    });
  }
}
